import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, ArrowRightLeft, RefreshCw } from 'lucide-react';
import { fmtDate } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_LABEL = {
  resident: 'CC Residente',
  insurer: 'CC Convenio',
  insurer_cc_resident: 'FC a Convenio · CC Residente',
};

const STATUS_META = {
  ok: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', Icon: CheckCircle2, label: 'OK' },
  unmatched: { cls: 'bg-amber-100 text-amber-700 border-amber-300', Icon: AlertTriangle, label: 'Sin match' },
  error: { cls: 'bg-rose-100 text-rose-700 border-rose-300', Icon: XCircle, label: 'Error' },
};

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function LegacyBulkImportPanel() {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null); // {draft_id, totals, rows}

  // v3.32 · Retarget a Curaduría
  const [allRows, setAllRows] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [tpps, setTpps] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [locationFilter, setLocationFilter] = useState('resident'); // all | resident | insurer | tpp
  const [textFilter, setTextFilter] = useState('');
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [targetTppId, setTargetTppId] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moving, setMoving] = useState(false);

  const loadAll = async () => {
    setLoadingAll(true);
    try {
      const [r, tr] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/billing/legacy-imports/all`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/health-insurers/third-party-payers`, { withCredentials: true }),
      ]);
      setAllRows(r.data?.rows || []);
      setTpps(tr.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando comprobantes legacy');
    } finally {
      setLoadingAll(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const filteredRows = allRows.filter((r) => {
    if (locationFilter !== 'all' && r.cc_location !== locationFilter) return false;
    if (textFilter) {
      const q = textFilter.toLowerCase();
      const hay = `${r.invoice_number || ''} ${r.resident_name || ''} ${r.cc_location_label || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const toggleSel = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const movable = filteredRows.filter((r) => r.cc_location === 'resident' && !r.has_payments && !r.cc_third_party_payer_id);
    const allSelected = movable.length > 0 && movable.every((r) => selectedIds.has(r.invoice_id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      movable.forEach((r) => {
        if (allSelected) next.delete(r.invoice_id); else next.add(r.invoice_id);
      });
      return next;
    });
  };

  const openMoveDialog = () => {
    if (selectedIds.size === 0) { toast.error('Seleccioná al menos un comprobante'); return; }
    if (tpps.length === 0) { toast.error('No hay convenios marcados como Pagador de Terceros (Curaduría)'); return; }
    setTargetTppId(tpps[0]?.insurer_id || '');
    setMoveReason('');
    setMoveDialogOpen(true);
  };

  const confirmMove = async () => {
    if (!targetTppId) { toast.error('Elegí la Curaduría destino'); return; }
    setMoving(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/billing/legacy-imports/retarget-to-tpp`,
        {
          invoice_ids: Array.from(selectedIds),
          target_tpp_insurer_id: targetTppId,
          reason: moveReason || null,
        },
        { withCredentials: true });
      toast.success(`✓ ${r.data.moved} comprobante(s) movidos a ${r.data.target_insurer_name}`);
      setSelectedIds(new Set());
      setMoveDialogOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo mover (rollback aplicado)');
    } finally {
      setMoving(false);
    }
  };

  const selectedTotal = Array.from(selectedIds)
    .map((id) => allRows.find((r) => r.invoice_id === id)?.total || 0)
    .reduce((a, b) => a + Number(b), 0);

  const onPreview = async () => {
    if (!file) { toast.error('Elegí un archivo XLSX'); return; }
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/billing/legacy-imports/bulk-xlsx/preview`, fd, { withCredentials: true });
      setPreview(r.data);
      toast.success(`Preview: ${r.data.totals.ok} listas, ${r.data.totals.unmatched} sin match, ${r.data.totals.error} con error`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en preview');
    } finally {
      setPreviewing(false);
    }
  };

  const onCommit = async () => {
    if (!preview?.draft_id) return;
    const okCount = preview.totals.ok;
    if (!window.confirm(`Vas a importar ${okCount} comprobante(s) por ${fmtMoney(preview.totals.total_amount_ok)}. ¿Confirmás?\n\nSi falla UNA sola fila, se revierten TODAS las anteriores (all-or-nothing).`)) return;
    setCommitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/billing/legacy-imports/bulk-xlsx/commit`,
        { draft_id: preview.draft_id }, { withCredentials: true });
      toast.success(`Importados ${r.data.inserted} comprobantes correctamente ✓`);
      setPreview(null);
      setFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al importar (rollback aplicado)');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card data-testid="legacy-bulk-panel" className="border-indigo-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-indigo-700" />
          Importación masiva de comprobantes legacy (XLSX)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Subí el XLSX de <b>IVA Ventas</b> del sistema anterior (columnas: Fecha · T · Comprobante · Cliente · Tipo/Nro.Doc. · RESIDENTE · COD · Neto · IVA · Exento · Total).
          El sistema clasifica cada fila automáticamente, muestra un preview, y recién inyecta cuando confirmás. Los asientos van a <b>Deudores por Ventas / Ajuste Transición Sistemas</b> sin tocar resultado del ejercicio ni el Libro IVA Ventas.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            className="text-xs"
            data-testid="legacy-bulk-file"
          />
          <Button size="sm" onClick={onPreview} disabled={!file || previewing} data-testid="legacy-bulk-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Previsualizar
          </Button>
          {preview && preview.totals.ok > 0 && (
            <Button size="sm" onClick={onCommit} disabled={committing} className="bg-indigo-700 hover:bg-indigo-800" data-testid="legacy-bulk-commit-btn">
              {committing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Importar {preview.totals.ok} filas
            </Button>
          )}
        </div>

        {/* v3.32 · Panel de retarget a Curaduría */}
        <div className="mt-4 pt-4 border-t border-slate-200" data-testid="legacy-retarget-section">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div>
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4 text-indigo-700" />
                Comprobantes ya importados · mover a Curaduría
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Lista todos los comprobantes <b>legacy</b> cargados (bulk + manuales). Marcá los que debían impactar en la
                CC de <b>Curaduría I o II</b> en lugar de la ficha del residente y pulsá «Mover». No se tocan los comprobantes
                fiscales (CAE/Nro intactos), sólo se transfiere el saldo pendiente a la CC del convenio curaduría.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={loadAll} disabled={loadingAll} data-testid="legacy-retarget-reload">
              {loadingAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Recargar
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-56 h-8 text-xs" data-testid="legacy-retarget-loc-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Destino actual: Todos</SelectItem>
                <SelectItem value="resident">Destino actual: Residente</SelectItem>
                <SelectItem value="insurer">Destino actual: Convenio</SelectItem>
                <SelectItem value="tpp">Destino actual: Curaduría</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder="Buscar: Nº, residente…"
              className="h-8 text-xs w-64"
              data-testid="legacy-retarget-text-filter"
            />
            <div className="ml-auto flex items-center gap-2">
              {selectedIds.size > 0 && (
                <span className="text-xs text-slate-600" data-testid="legacy-retarget-selected-count">
                  {selectedIds.size} selec. · {fmtMoney(selectedTotal)}
                </span>
              )}
              <Button
                size="sm"
                onClick={openMoveDialog}
                disabled={selectedIds.size === 0}
                className="bg-indigo-700 hover:bg-indigo-800"
                data-testid="legacy-retarget-move-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-1" />
                Mover a Curaduría
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[420px] overflow-y-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50 z-10">
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        filteredRows.length > 0 &&
                        filteredRows.filter((r) => r.cc_location === 'resident' && !r.has_payments).every((r) => selectedIds.has(r.invoice_id))
                      }
                      onCheckedChange={toggleAllVisible}
                      data-testid="legacy-retarget-sel-all"
                    />
                  </TableHead>
                  <TableHead>Comprobante</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Residente</TableHead>
                  <TableHead>Destino CC actual</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingAll ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">Cargando…</TableCell></TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-6">Sin comprobantes para el filtro aplicado.</TableCell></TableRow>
                ) : filteredRows.map((r) => {
                  const movable = r.cc_location === 'resident' && !r.has_payments && !r.cc_third_party_payer_id;
                  const checked = selectedIds.has(r.invoice_id);
                  return (
                    <TableRow key={r.invoice_id} data-testid={`legacy-retarget-row-${r.invoice_number}`}>
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          disabled={!movable}
                          onCheckedChange={() => toggleSel(r.invoice_id)}
                          data-testid={`legacy-retarget-sel-${r.invoice_number}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                      <TableCell className="text-xs">{fmtDate(r.issue_date)}</TableCell>
                      <TableCell className="text-xs">{r.resident_name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-xs">
                        <Badge
                          variant="outline"
                          className={
                            r.cc_location === 'tpp' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]'
                            : r.cc_location === 'insurer' ? 'bg-amber-50 text-amber-700 border-amber-200 text-[10px]'
                            : 'bg-slate-50 text-slate-700 border-slate-200 text-[10px]'
                          }
                        >
                          {r.cc_location_label}
                        </Badge>
                        {r.has_payments && (
                          <div className="text-[10px] text-rose-600 mt-0.5">Tiene cobros/créditos aplicados</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs">{fmtMoney(r.total)}</TableCell>
                      <TableCell className="text-right text-xs font-semibold">{fmtMoney(r.outstanding)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        <Dialog open={moveDialogOpen} onOpenChange={(v) => !moving && setMoveDialogOpen(v)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Mover {selectedIds.size} comprobante(s) a Curaduría</DialogTitle>
              <DialogDescription>
                El saldo pendiente se transfiere a la CC del convenio curaduría elegido. El comprobante fiscal
                no se modifica (sigue a nombre del residente).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs font-medium">Curaduría destino *</label>
                <Select value={targetTppId} onValueChange={setTargetTppId}>
                  <SelectTrigger data-testid="legacy-retarget-tpp-select"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                  <SelectContent>
                    {tpps.map((t) => <SelectItem key={t.insurer_id} value={t.insurer_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Motivo (opcional)</label>
                <Textarea
                  rows={2}
                  value={moveReason}
                  onChange={(e) => setMoveReason(e.target.value)}
                  placeholder="Ej: reclasificación migración"
                  data-testid="legacy-retarget-reason"
                />
              </div>
              <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2 text-xs text-indigo-800">
                Total a transferir: <b>{fmtMoney(selectedTotal)}</b>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveDialogOpen(false)} disabled={moving}>Cancelar</Button>
              <Button onClick={confirmMove} disabled={moving || !targetTppId} className="bg-indigo-700 hover:bg-indigo-800" data-testid="legacy-retarget-confirm-btn">
                {moving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
                Confirmar movimiento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {preview && (
          <>
            <div className="grid grid-cols-4 gap-2">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-center" data-testid="legacy-kpi-ok">
                <div className="text-[10px] text-emerald-700">LISTAS</div>
                <div className="text-lg font-bold text-emerald-700">{preview.totals.ok}</div>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-center" data-testid="legacy-kpi-unmatched">
                <div className="text-[10px] text-amber-700">SIN MATCH</div>
                <div className="text-lg font-bold text-amber-700">{preview.totals.unmatched}</div>
              </div>
              <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-center" data-testid="legacy-kpi-error">
                <div className="text-[10px] text-rose-700">ERRORES</div>
                <div className="text-lg font-bold text-rose-700">{preview.totals.error}</div>
              </div>
              <div className="rounded-md border border-indigo-200 bg-indigo-50 p-2 text-center" data-testid="legacy-kpi-amount">
                <div className="text-[10px] text-indigo-700">IMPORTE OK</div>
                <div className="text-base font-bold text-indigo-700">{fmtMoney(preview.totals.total_amount_ok)}</div>
              </div>
            </div>

            <div className="overflow-x-auto max-h-[480px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-50">
                  <TableRow>
                    <TableHead className="w-16">Fila</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Comprobante</TableHead>
                    <TableHead>COD</TableHead>
                    <TableHead>Destino CC</TableHead>
                    <TableHead>Cliente / Residente</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r, idx) => {
                    const meta = STATUS_META[r.status] || STATUS_META.error;
                    const Icon = meta.Icon;
                    return (
                      <TableRow key={idx} data-testid={`legacy-row-${idx}`}>
                        <TableCell className="text-xs text-muted-foreground">{r.excel_row || idx}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>
                            <Icon className="w-3 h-3 mr-1" />{meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{r.issue_date || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{r.number || r.source?.Comprobante || '—'}</TableCell>
                        <TableCell className="text-xs">{r.cod ?? '—'}</TableCell>
                        <TableCell className="text-xs">
                          {r.mode ? <Badge variant="outline" className="text-[10px]">{MODE_LABEL[r.mode] || r.mode}</Badge> : '—'}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium">{r.resident_name || r.insurer_name || r.cliente || '—'}</div>
                          {r.residente_label && r.residente_label !== 'CONVENIO' && (
                            <div className="text-[10px] text-slate-500">{r.residente_label}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs font-semibold">{fmtMoney(r.total)}</TableCell>
                        <TableCell className="text-[11px] text-rose-700">{r.reason || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
